import { ScreenPermission } from './model/capabilities'
import { GoFullscreenMessage } from './model/presenter-messages'
import type { Locks } from './replication/peer-presence'

/**
 * The browser side of presenter mode, kept thin: everything that decides
 * lives in `model/`. Rules every caller relies on (verified on Chrome 156,
 * docs/plans/in-person-presenter-mode.md 4.2-4.3):
 *
 * - `window.open` and `delegateFullscreen` each need the click they are
 *   called in, and each uses it up - never both in one click, never after an
 *   `await`.
 * - `getScreenDetails` needs a click while the permission is still `prompt`.
 */

export const hasWindowManagement = (): boolean =>
	typeof window.getScreenDetails === 'function'

export const isExtendedDisplay = (): boolean | undefined =>
	window.screen.isExtended

type PermissionQuery = (descriptor: {
	name: string
}) => Promise<PermissionStatus>

/**
 * The screen permission. Chrome renamed it from `window-placement` to
 * `window-management` in 111; older names are tried in turn, and anything
 * that cannot be asked counts as `unknown`.
 */
export const queryScreenPermission = async (): Promise<{
	state: ScreenPermission
	status?: PermissionStatus
}> => {
	if (!navigator.permissions) {
		return { state: 'unknown' }
	}
	// `PermissionName` does not list these names yet.
	const query = navigator.permissions.query as PermissionQuery
	for (const name of ['window-management', 'window-placement']) {
		try {
			const status = await query.call(navigator.permissions, { name })
			return { state: status.state, status }
		} catch (e) {
			// Unknown name in this browser: try the next one.
		}
	}
	return { state: 'unknown' }
}

/** The screens, prompting for the permission if needed (needs a click then). */
export const readScreens = async (): Promise<ScreenDetails | undefined> => {
	if (!window.getScreenDetails) {
		return undefined
	}
	try {
		return await window.getScreenDetails()
	} catch (e) {
		return undefined
	}
}

export const openProjectorWindow = (
	url: string,
	name: string,
	features: string
): Window | null => window.open(url, name, features)

/**
 * Hands this click's right to go full screen to the presentation window. A
 * browser without Capability Delegation ignores `delegate` and delivers a
 * plain message; the presentation window's own Full screen button covers it.
 */
export const delegateFullscreen = (
	target: Window,
	message: GoFullscreenMessage
): void =>
	target.postMessage(message, {
		targetOrigin: window.location.origin,
		delegate: 'fullscreen',
	})

export const enterOwnFullscreen = (): Promise<void> =>
	document.documentElement.requestFullscreen()

export const leaveOwnFullscreen = (): Promise<void> =>
	document.fullscreenElement ? document.exitFullscreen() : Promise.resolve()

export const isOwnFullscreen = (): boolean => !!document.fullscreenElement

/**
 * The Web Locks API (every current browser), for the link's presence. Read
 * through a cast: the TypeScript that compiles the tests (4.5) predates its
 * declaration.
 */
export const webLocks = (): Locks | undefined =>
	(navigator as Navigator & { locks?: Locks }).locks

/** Whether this click can still open a window or delegate (Chrome 72+). */
export const clickStillActive = (): boolean => {
	const activation = (
		navigator as Navigator & {
			userActivation?: { isActive: boolean }
		}
	).userActivation
	return activation ? activation.isActive : true
}
